import {
    ChangeDetectorRef, Directive,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from "@angular/core";
import {SearchModel} from "@fedeltapointofsale/data-client";
import {ControlValueAccessor, FormControl} from "@angular/forms";
import {debounceTime, distinctUntilChanged, forkJoin, map, of, Subject, Subscription} from "rxjs";
import {SearchControlSelectObjects} from "./search-control-select-objects";
import {TranslateService} from "@ngx-translate/core";
import {EntityFormControl} from "../edit/entity-form-builder/entity-form-control";

/**
 * Selector used to find select controls. Each sub-classes should provide itself with this selector.
 * @type {string}
 */
export class SelectControlSelector {
}

export enum SelectControlMode {
    Default,
    Autocomplete,
    Navigation
}

@Directive()
export class AbstractSelectControlComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
    static readonly LIMIT_FETCH_SIZE = 30;

    @Input() labelField: string;
    @Input() valueField: string;
    @Input() isSearchable: boolean = true;
    @Input() defaultValue: any;
    @Input() extendedSearchComponent: any;
    @Input() extendedSearchComponentParams: any;
    @Input() extendedEditComponent: any;
    @Input() extendedEditComponentParams: any;
    @Input() labelSearchField: string;
    @Input() valueSearchField: string;
    @Input() formControlValidation: FormControl;
    @Input() data: Array<any>;
    @Input() sourceSearchModel: SearchModel<any>;
    @Input() public label: string;
    @Input() public placeholder: string;
    @Input() public readonly: boolean;
    @Input() mode: SelectControlMode = SelectControlMode.Default;
    @Input() removable: boolean;
    @Input() showOptionOpenOption: boolean = false;
    @Input() hideOptionOpenOption: boolean = false;

    /**
     * Override the default required that is managed by the validation framework
     */
    @Input() public overrideRequired: boolean;

    @Output() onDataLoaded: EventEmitter<any> = new EventEmitter();
    @Output() onOptionOpen: EventEmitter<any> = new EventEmitter();
    @Output() valueChange: EventEmitter<any> = new EventEmitter();
    @Output() focussed: EventEmitter<boolean> = new EventEmitter();
    @Output() removed: EventEmitter<any> = new EventEmitter();

    isLoading: boolean;
    isFocussed: boolean;
    customFiltering: boolean;
    firstLoad: boolean = true;
    totalRecords: number;
    private dataInitialised: boolean;

    private localFilterWatcher: Subject<string> = new Subject<string>();

    protected subscriptions: Subscription = new Subscription();

    _value: any;

    propagateChange: any = () => {
    };

    constructor(protected changeDetector: ChangeDetectorRef, protected translateService: TranslateService) {
    }

    /**
     *
     * @returns {any}
     */
    get value(): any {
        return this._value;
    }

    /**
     *
     * @param value
     */
    @Input()
    set value(value: any) {
        if (!this.valueEqual(value, this._value)) {
            this._value = value;
            this.propagateChange(value);
            this.valueChange.emit(value);
        }
    }

    ngOnInit(): void {
        this.localFilterWatcher.pipe(
            debounceTime(400),
            distinctUntilChanged()
        ).subscribe((searchValue) => {
            // if it is a auto complete, only search when the value is not empty
            if (this.mode === SelectControlMode.Autocomplete) {
                this.data = undefined;
                if (searchValue != undefined && searchValue !== "") {
                    this.sourceSearchModel.filter[this.labelSearchField || this.labelField] = searchValue;
                    this.loadDataFromSearchModel();
                }
            } else if (this.customFiltering) {
                this.sourceSearchModel.filter[this.labelSearchField || this.labelField] = searchValue;
                this.loadDataFromSearchModel();
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    loadDataFromSearchModel(): void {
        if (!this.sourceSearchModel) {
            return;
        }

        this.subscriptions.add(this.sourceSearchModel.filterChange.subscribe(() => {
            this.dataInitialised = false;
            this.firstLoad = true;
            this.data = undefined;
        }));

        this.sourceSearchModel.limit = AbstractSelectControlComponent.LIMIT_FETCH_SIZE;
        this.isLoading = true;
        this.sourceSearchModel.search().subscribe(() => {
            this.data = this.sourceSearchModel.data;
            this.onDataLoaded.emit(this.data);

            forkJoin(
                this.sourceSearchModel.data.map(datum =>
                    datum[this.labelField] && datum[this.labelField].startsWith && datum[this.labelField].startsWith("--i18n--") ?
                        this.translateService.get(datum[this.labelField].substring(8)).pipe(
                            map(translation => ({
                                [this.labelField]: translation,
                                [this.valueField]: datum[this.valueField]
                            }))
                        )
                        : of(datum)
                )
            ).subscribe(results => this.data = results);

            this.isLoading = false;
            this.totalRecords = this.sourceSearchModel.total;
            // In case the first load test if it needs to fetch the data when searching
            // or just search in memory
            if (this.firstLoad && (!this.sourceSearchModel.total || this.sourceSearchModel.total < AbstractSelectControlComponent.LIMIT_FETCH_SIZE)) {
                this.customFiltering = false;
            }
            this.firstLoad = false;
            if (!this.changeDetector["destroyed"]) {
                try {
                    this.changeDetector.detectChanges();
                } catch (e) {
                }
            }
        }, () => {
            this.isLoading = false;
            if (!this.changeDetector["destroyed"]) {
                try {
                    this.changeDetector.detectChanges();
                } catch (e) {
                }
            }
        });
    }

    filtered(filter: string): void {
        this.localFilterWatcher.next(filter);
    }

    /**
     * Compare if the values are (sort of) equal
     * @param value1
     * @param value2
     * @returns {boolean}
     */
    valueEqual(value1: any, value2: any): boolean {
        return (this.isEmptyArray(value1) && this.isEmptyArray(value2)) || value1 == value2;
    }

    isEmptyArray(value: any): boolean {
        return (!!value) && (value.constructor === Array) && value.length === 0;
    }

    /**
     *
     * @param obj
     */
    writeValue(obj: any): void {
        this._value = obj;
    }

    /**
     *
     * @param fn
     */
    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    /**
     *
     * @param fn
     */
    registerOnTouched(fn: any): void {
    }

    /**
     *
     * @param isDisabled
     */
    setDisabledState(isDisabled: boolean): void {
    }

    onRemove(): void {
        this.removed.emit();
    }

    public refreshData() {
        this.dataInitialised = false;
    }

    onFocus(focussed: boolean): void {
        this.isFocussed = focussed;
        this.focussed.next(focussed);
        if (!this.dataInitialised && this.sourceSearchModel && this.mode !== SelectControlMode.Autocomplete) {
            this.customFiltering = true;
            this.loadDataFromSearchModel();
            this.dataInitialised = true;
        }
    }

    setSearchControlSelectObjects(searchControlSelectObjects: SearchControlSelectObjects): void {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["overrideRequired"]) {
            if (this.overrideRequired != undefined && this.formControlValidation instanceof EntityFormControl) {
                this.formControlValidation.overrideRequired = this.overrideRequired;
            }
        }
    }
}
